/** Gate checklist (A2 slice 2) — the hard stop before a gated bankruptcy-route move. System items are checked by the portal,
 *  attested items are ticked by the handler; the Head of Legal can override non-statutory items with a reason. */
import { useEffect, useState } from "react";
import { ArrowRight, Ban, CheckCircle2, CircleDashed, Loader2, ShieldAlert, ShieldCheck, Undo2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { attestGateItem, getCaseGates, moveCaseRoute, overrideGateItem } from "@/lib/api";
import ServiceRecordDialog from "@/pages/legal/ServiceRecordDialog";
import RegisterCheckForm, { RegisterCheckEvidence } from "@/pages/legal/RegisterCheckForm";

const STATUS = {
  pass: { icon: CheckCircle2, cls: "text-emerald-600", label: "OK" },
  fail: { icon: XCircle, cls: "text-rose-600", label: "Open" },
  overridden: { icon: ShieldAlert, cls: "text-amber-600", label: "Overridden" },
  disabled: { icon: Ban, cls: "text-slate-400", label: "Off" },
};

function GateItemRow({ caseRow, gate, item, latestCheck, onUpdate, onRecordService }) {
  const [busy, setBusy] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [reason, setReason] = useState("");
  const S = STATUS[item.status] || STATUS.fail;
  const run = async (fn, ok) => {
    setBusy(true);
    try { onUpdate(await fn()); if (ok) toast.success(ok); setOverriding(false); setReason(""); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <li className={cn("px-3 py-2", item.status === "fail" && "bg-rose-50/40")} data-testid={`gate-row-${item.id}`}>
      <div className="flex items-start gap-2">
        {item.kind === "attested" ? (
          <input type="checkbox" className="mt-0.5" checked={item.status === "pass" || item.status === "overridden"} disabled={busy || item.status === "overridden"}
            onChange={(e) => run(() => attestGateItem(caseRow.case_id, gate, item.id, e.target.checked))} data-testid={`gate-attest-${item.id}`} />
        ) : (
          <S.icon className={cn("w-4 h-4 mt-0.5 shrink-0", S.cls)} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn("text-xs", item.status === "pass" && item.kind === "attested" && "text-slate-500")}>{item.label}</span>
            <span className={cn("text-[9px] uppercase px-1.5 py-0.5 rounded border", item.kind === "system" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-emerald-50 text-emerald-700 border-emerald-200")}>{item.kind}</span>
            {!item.overridable && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300" title="Statutory — cannot be overridden">statutory</span>}
            <span className={cn("ml-auto text-[10px] font-medium", S.cls)} data-testid={`gate-status-${item.id}`}>{S.label}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`gate-detail-${item.id}`}>{item.detail}</div>
          {item.override && (
            <div className="text-[11px] text-amber-800 mt-0.5" data-testid={`gate-override-note-${item.id}`}>
              ⚠ Overridden by {item.override.by} on {new Date(item.override.at).toLocaleDateString("en-GB")} — “{item.override.reason}”
              {item.can_override && (
                <button type="button" className="ml-2 inline-flex items-center gap-0.5 text-[10px] underline" disabled={busy}
                  onClick={() => run(() => overrideGateItem(caseRow.case_id, gate, item.id, null, true), "Override removed")} data-testid={`gate-override-clear-${item.id}`}>
                  <Undo2 className="w-3 h-3" />remove
                </button>
              )}
            </div>
          )}
          {item.id === "sys_register_check" && item.status === "pass" && <RegisterCheckEvidence check={latestCheck} />}
          {item.status === "fail" && item.action === "register_check" && <RegisterCheckForm caseId={caseRow.case_id} onDone={onUpdate} />}
          {item.status === "fail" && item.action === "service_record" && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] mt-1.5 border-orange-300 text-orange-800" onClick={onRecordService} data-testid="gate-record-service-btn">Record service…</Button>
          )}
          {item.status === "fail" && item.can_override && !item.action && !overriding && (
            <button type="button" className="mt-1.5 text-[10px] text-amber-700 underline inline-flex items-center gap-1" onClick={() => setOverriding(true)} data-testid={`gate-override-btn-${item.id}`}>
              <ShieldAlert className="w-3 h-3" />Override as Head of Legal…
            </button>
          )}
          {overriding && (
            <div className="mt-1.5 space-y-1" data-testid={`gate-override-form-${item.id}`}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="text-xs" placeholder="Why this item can be waved through (logged on the file and in the audit log)" data-testid={`gate-override-reason-${item.id}`} />
              <div className="flex gap-1">
                <Button size="sm" className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700" disabled={busy || reason.trim().length < 5}
                  onClick={() => run(() => overrideGateItem(caseRow.case_id, gate, item.id, reason), "Item overridden")} data-testid={`gate-override-confirm-${item.id}`}>Confirm override</Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => { setOverriding(false); setReason(""); }}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default function GateChecklistDialog({ caseRow, gate, move, onMoved, onClose, onPassed, proceedLabel }) {
  const [data, setData] = useState(null);
  const [service, setService] = useState(false);
  const [moving, setMoving] = useState(false);
  const load = async () => { try { setData(await getCaseGates(caseRow.case_id)); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseRow.case_id]);
  const ev = data?.gates?.[gate];
  const latestCheck = [...(data?.register_checks || [])].sort((a, b) => (b.searched_at || "").localeCompare(a.searched_at || ""))[0];

  const doMove = async () => {
    setMoving(true);
    try {
      const res = await moveCaseRoute(caseRow.case_id, move.to, null);
      toast.success(`Bankruptcy route → ${res.route?.label || move.label}`);
      onMoved?.(res);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.gate) { setData((cur) => ({ ...cur, gates: { ...cur.gates, [gate]: d.gate } })); toast.error(d.message); }
      else toast.error(d?.message || d || e.message);
    } finally { setMoving(false); }
  };

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gate-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              {ev?.passed ? <ShieldCheck className="w-4 h-4 text-emerald-600" /> : <ShieldAlert className="w-4 h-4 text-rose-600" />}
              <span className="font-mono text-xs px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-700 border-indigo-200">{gate}</span>
              {ev?.label || "Gate"} <span className="text-[11px] font-normal text-muted-foreground">· {ev?.guards}</span>
            </DialogTitle>
          </DialogHeader>
          {!ev ? (
            <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Checking the file…</div>
          ) : (
            <>
              <div className={cn("text-xs rounded border px-3 py-2", ev.passed ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900")} data-testid="gate-summary">
                {ev.passed ? "Every item is satisfied — the file can move on." : `${ev.blocking.length} open item${ev.blocking.length === 1 ? "" : "s"} block this move.`}
                {ev.non_overridable_blocking.length > 0 && <span className="block text-[11px] mt-0.5">Statutory (cannot be overridden): {ev.non_overridable_blocking.join("; ")}</span>}
              </div>
              <ul className="divide-y border rounded" data-testid="gate-items">
                {ev.items.map((it) => (
                  <GateItemRow key={it.id} caseRow={caseRow} gate={gate} item={it} latestCheck={latestCheck} onUpdate={setData} onRecordService={() => setService(true)} />
                ))}
                {ev.items.length === 0 && <li className="px-3 py-2 text-xs italic text-muted-foreground flex items-center gap-1"><CircleDashed className="w-3.5 h-3.5" />No checklist items configured — this gate is open.</li>}
              </ul>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={onClose} data-testid="gate-close">Close</Button>
            {!move && onPassed && (
              <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={onPassed} disabled={!ev?.passed} data-testid="gate-proceed-btn">
                <ArrowRight className="w-3.5 h-3.5 mr-1" />{proceedLabel || "Continue"}
              </Button>
            )}
            {move && (
              <Button size="sm" className="bg-red-700 hover:bg-red-800" onClick={doMove} disabled={!ev?.passed || moving} data-testid="gate-move-btn">
                {moving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5 mr-1" />}Move to {move.label}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {service && (
        <ServiceRecordDialog caseRow={caseRow} methods={data?.service_methods || []} onClose={() => setService(false)}
          onSaved={(r) => { setService(false); if (r.route) onMoved?.({ route: r.route, created_events: r.created_events }); else load(); }} />
      )}
    </>
  );
}
